'use client';

import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { useJobportalOffersStore } from '@eliadmin/core/src/stores/jobportal-offers-store';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchLanguages } from '@eliadmin/core/src/api/general/general-api';
import { fetchJobCategories } from '@eliadmin/core/src/api/jobportal/categories-api';
import { fetchEmploymentTypes } from '@eliadmin/core/src/api/jobportal/create-curriculum-api';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { CheckboxesFilter } from '../../../filter/fields/jobportal-filter-checkboxes';
import { RangeFilter } from '../../../filter/fields/jobportal-filter-range';

export const JobOffersFilterContent = () => {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const { setFilter, filter, salaryRanges } = useJobportalOffersStore();

  const { data: categories } = useQuery({
    queryKey: [CacheKey.JOBPORTAL_CATEGORIES],
    queryFn: () =>
      fetchJobCategories({ locale, query: { results: 150 } }).then(r =>
        r.data.map(g => ({ label: g.name, value: g.id.toString() })),
      ),
  });

  const { data: employmentTypes } = useQuery({
    queryKey: [CacheKey.JOBPORTAL_EMPLOYMENT_TYPES],
    queryFn: () =>
      fetchEmploymentTypes({ locale }).then(r =>
        r.data.map(g => ({ label: g.name, value: g.id.toString() })),
      ),
  });

  const { data: languages } = useQuery({
    queryKey: [CacheKey.LANGUAGES],
    queryFn: () =>
      fetchLanguages().then(r => r.data.map(g => ({ label: g.name, value: g.id.toString() }))),
  });

  const currency = useGlobalStore(s => s.generalLayout.currency);

  const [salaryRange, setSalaryRange] = useState<string[]>([
    salaryRanges?.overall.min ?? '0',
    salaryRanges?.overall.min ?? '5000',
  ]);

  const [definedSalaryRanges, setDefinedSalaryRanges] = useState<{
    min: string;
    max: string;
  }>({
    min: salaryRanges?.overall.min || '0',
    max: salaryRanges?.overall.max || '5000',
  });

  const handleSalaryChange = (newSalary: number[]) => {
    setFilter({ min_salary: newSalary[0].toString(), max_salary: newSalary[1].toString() });
    setSalaryRange(newSalary.map(val => val.toString()));
  };

  useEffect(() => {
    if (
      !filter.salary_types?.length ||
      (filter.salary_types?.includes('1') && filter.salary_types?.includes('2'))
    ) {
      setDefinedSalaryRanges({
        min: salaryRanges?.overall.min || '0',
        max: salaryRanges?.overall.max || '5000',
      });
      setFilter({ min_salary: salaryRanges?.overall.min, max_salary: salaryRanges?.overall.max });
      setSalaryRange([salaryRanges?.overall.min ?? '0', salaryRanges?.overall.max ?? '5000']);
      return;
    }
    if (filter.salary_types?.includes('1')) {
      setDefinedSalaryRanges({
        min: salaryRanges?.['1'].min || '0',
        max: salaryRanges?.['1'].max || '5000',
      });
      setFilter({ min_salary: salaryRanges?.['1'].min, max_salary: salaryRanges?.['1'].max });
      setSalaryRange([salaryRanges?.['1'].min ?? '0', salaryRanges?.['1'].max ?? '5000']);
      return;
    }
    if (filter.salary_types?.includes('2')) {
      setDefinedSalaryRanges({
        min: salaryRanges?.['2'].min || '0',
        max: salaryRanges?.['2'].max || '5000',
      });
      setFilter({ min_salary: salaryRanges?.['2'].min, max_salary: salaryRanges?.['2'].max });
      setSalaryRange([salaryRanges?.['2'].min ?? '0', salaryRanges?.['2'].max ?? '5000']);
      return;
    }
  }, [filter.salary_types, salaryRanges]);

  return (
    <>
      {categories && (
        <CheckboxesFilter
          title={t('text176')}
          options={categories}
          selectedOptions={
            categories
              .filter(val => filter.categories?.find(val2 => val2 === val.value))
              .map(val3 => val3.value) ?? []
          }
          onChange={(newCategories: any) => {
            setFilter({ categories: newCategories });
          }}
          search
          searchPlaceholder={'Vyhľadať kategóriu'}
          sortBySelected
        />
      )}
      <Separator className="bg-[#D8D8DB]" />
      <div className="flex flex-col">
        <div className="w-full text-start text-lg font-semibold text-[#63626F]">Plat</div>
        <RangeFilter
          value={salaryRange.map(val => parseInt(val))}
          min={definedSalaryRanges.min ? parseInt(definedSalaryRanges.min) : 0}
          max={definedSalaryRanges.max ? parseInt(definedSalaryRanges.max) : 5000}
          onValueChange={handleSalaryChange}
          formatValue={(salary: any) => salary.toLocaleString()}
          currency={currency}
          templateSetting={{ enabled: true }}
        />
      </div>
      <Separator className="bg-[#D8D8DB]" />
      {employmentTypes && (
        <CheckboxesFilter
          title={t('text894')}
          options={employmentTypes}
          selectedOptions={
            employmentTypes
              .filter(val => filter.employment_types?.find(val2 => val2 === val.value))
              .map(val3 => val3.value) ?? []
          }
          onChange={(newEmploymentTypes: any) => {
            setFilter({ employment_types: newEmploymentTypes });
          }}
        />
      )}
      <Separator className="bg-[#D8D8DB]" />
      {languages && (
        <CheckboxesFilter
          title={t('text1612')}
          options={languages}
          selectedOptions={
            languages
              .filter(val => filter.language?.find(val2 => val2 === val.value))
              .map(val3 => val3.value) ?? []
          }
          onChange={(newLanguages: any) => {
            setFilter({ language: newLanguages });
          }}
        />
      )}
      <Separator className="bg-[#D8D8DB]" />
      <div className="flex flex-col">
        <div className="w-full text-start text-lg font-semibold text-[#63626F]">{t('text254')}</div>
        <CheckboxesFilter
          title=""
          options={[{ label: t('Duálne vzdelávanie'), value: '1' }]}
          selectedOptions={filter.dual_education ? ['1'] : []}
          onChange={(dual_education: any) => {
            if (dual_education.includes('1')) {
              setFilter({ dual_education: true });
            } else {
              setFilter({ dual_education: false });
            }
          }}
        />
      </div>
    </>
  );
};
